import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, TextField, InputAdornment,
  CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import { ShoppingCart, Search, WhatsApp, Phone, ContentCopy } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function ReorderPage() {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/inventory/reorder');
      setProducts(data.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.supplier_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(p => p.id)));
  const clearAll = () => setSelected(new Set());

  // Group by supplier
  const bySupplier = filtered.reduce((acc, p) => {
    const key = p.supplier_name || 'No Supplier';
    if (!acc[key]) acc[key] = { supplier: key, phone: p.supplier_phone, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  const sendSupplierWA = async (supplier, phone) => {
    if (!phone) {
      dispatch(showSnackbar({ message: 'No phone on file for this supplier', severity: 'warning' }));
      return;
    }
    const items = (bySupplier[supplier]?.items || []).map(p =>
      `• ${p.name}${p.sku ? ` (${p.sku})` : ''}: Current ${p.stock}, Need ${Math.max(1, p.shortage)} units`
    ).join('\n');
    const message = `السلام علیکم,\n\nPlease supply the following items urgently:\n\n${items}\n\nKindly confirm availability and pricing.\n\nRegards.`;
    try {
      await api.post('/whatsapp/send', { phone, message });
      dispatch(showSnackbar({ message: `Order request sent to ${supplier}`, severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'WhatsApp send failed', severity: 'error' }));
    }
  };

  const urgencyColor = (shortage) => {
    if (shortage >= 20) return 'error';
    if (shortage >= 10) return 'warning';
    return 'info';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShoppingCart color="primary" />
          <Typography variant="h5" fontWeight={700}>Reorder Suggestions / دوبارہ آرڈر</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Chip label={`${selected.size} selected`} color="primary" onDelete={clearAll} />
          )}
          <Button variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      {!loading && products.length === 0 && (
        <Alert severity="success">All products are adequately stocked — no reorders needed 🎉</Alert>
      )}

      {!loading && products.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{products.length}</strong> product(s) are at or below their reorder threshold.
          Grouped by supplier for easy ordering.
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <TextField
            fullWidth size="small" placeholder="Search product, SKU, or supplier…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        Object.entries(bySupplier).map(([supplierName, group]) => (
          <Card key={supplierName} sx={{ mb: 2 }}>
            {/* Supplier header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ShoppingCart color="primary" fontSize="small" />
                <Typography fontWeight={700}>{supplierName}</Typography>
                {group.phone && (
                  <Chip icon={<Phone />} label={group.phone} size="small" variant="outlined" />
                )}
                <Chip label={`${group.items.length} items`} size="small" color="warning" />
              </Box>
              {group.phone && (
                <Tooltip title={`Send WhatsApp order to ${supplierName}`}>
                  <Button
                    size="small" startIcon={<WhatsApp />} color="success" variant="outlined"
                    onClick={() => sendSupplierWA(supplierName, group.phone)}
                    sx={{ borderColor: '#25D366', color: '#25D366', '&:hover': { bgcolor: '#25D36610', borderColor: '#25D366' } }}
                  >
                    Order via WA
                  </Button>
                </Tooltip>
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Current Stock</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Min Level</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Shortage</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Buy Price</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Urgency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.items.map(p => (
                  <TableRow key={p.id} hover
                    sx={{ bgcolor: p.stock === 0 ? '#fef2f2' : p.shortage > 10 ? '#fff7ed' : 'inherit' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{p.sku || '—'}</Typography>
                    </TableCell>
                    <TableCell>{p.category || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={p.stock === 0 ? 'error.main' : 'text.primary'}>
                        {p.stock}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{p.low_stock_alert}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="error.main">+{Math.max(0, p.shortage)}</Typography>
                    </TableCell>
                    <TableCell align="right">₨ {fmt(p.purchase_price)}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.stock === 0 ? 'OUT OF STOCK' : `Low — ${p.shortage} short`}
                        size="small"
                        color={p.stock === 0 ? 'error' : urgencyColor(p.shortage)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {/* Subtotal cost row */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={6} />
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">Est. reorder cost:</Typography>
                    <Typography fontWeight={700}>
                      ₨ {fmt(group.items.reduce((s, p) => s + +p.purchase_price * Math.max(0, p.shortage), 0))}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        ))
      )}
    </Box>
  );
}
